/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react"
import styles from "./NoteFinderPageOverlay.module.scss"
import {State} from "ui/feature/root/FormReducer";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import NoteFinderPageOverlayNote from "ui/feature/root/note-select/NoteFinderPageOverlayNote";
import PageOverlayLoading from "ui/feature/root/common/PageOverlayLoading";
import NoteFinderPageBarLineMask from "ui/feature/root/note-select/NoteFinderPageBarLineMask";
import NoteFinderPageOverlaySharpFlatNatural from "ui/feature/root/note-select/NoteFinderPageOverlaySharpFlatNatural";
import NoteFinderPageOverlayTie from "ui/feature/root/note-select/NoteFinderPageOverlayTie";
import {
    NoteFinderPageOverlay_sheet$data
} from "ui/feature/root/note-select/__generated__/NoteFinderPageOverlay_sheet.graphql";

interface Props {
    state: State;
    sheet: NoteFinderPageOverlay_sheet$data
}

const NoteFinderPageOverlay: React.FC<Props> = ({sheet}) => {

    const imageWidth = sheet.width;
    const imageHeight = sheet.height;

    const isLoading: boolean = React.useMemo(() => {
        if (!sheet.note_combine_steps) {
            console.log("no no combine steps", sheet);
            return true;
        }
        const step = sheet.note_combine_steps[0];
        console.log("have step", step);
        return !step.data;
    }, [sheet]);

    const isError: boolean = React.useMemo(() => {
        if (!sheet.note_combine_steps) {
            return false;
        }
        return sheet.note_combine_steps[0].status === "ERROR";
    }, [sheet]);

    const barLines = React.useMemo(() => {
        if (!sheet.bar_line_processor_steps || sheet.bar_line_processor_steps.length === 0) {
            return [];
        }
        console.log(sheet.bar_line_processor_steps);
        const output = sheet.bar_line_processor_steps[0].data?.masks || [];
        if (output) {
            return output;
        } else {
            return [];
        }
    }, [sheet])

    const notes = React.useMemo(() => {
        if (!sheet.note_combine_steps) {
            return [];
        }
        const output = sheet.note_combine_steps[0].data?.notes || []
        if (output) {
            return output;
        } else {
            return [];
        }
    }, [sheet]);

    const ties = React.useMemo(() => {
        if (!sheet.tie_finder_steps) {
            return [];
        }
        const output = sheet.tie_finder_steps[0].data?.ties || [];
        if (output) {
            return output
        } else {
            return [];
        }
    }, [sheet])

    const sharpFlatNaturals = React.useMemo(() => {
        if (!sheet.note_combine_steps) {
            return [];
        }
        const output = sheet.note_combine_steps[0].data?.sharp_flat_naturals || [];
        if (output) {
            return output;
        } else {
            return [];
        }
    }, [sheet])

    if (isError) {
        return <p>TODO: An error state</p>;
    } else if (isLoading) {
        return <PageOverlayLoading/>;
    }

    return (
        <div className={styles.overlayContainer}>
            {barLines.map((barLine) => <NoteFinderPageBarLineMask key={barLine.key} mask={barLine}/>)}
            {notes.map((note) =>
                <NoteFinderPageOverlayNote pageWidth={imageWidth} pageHeight={imageHeight} key={note.note_id}
                                           note={note}/>)}
            {sharpFlatNaturals.map((sharpFlatNatural) =>
                <NoteFinderPageOverlaySharpFlatNatural
                    sharpFlatNatural={sharpFlatNatural}
                    pageHeight={imageHeight}
                    pageWidth={imageWidth}
                    key={sharpFlatNatural.id}/>)}
            {ties.map((tie) => <NoteFinderPageOverlayTie key={tie.id} tie={tie} pageWidth={imageWidth}
                                                         pageHeight={imageHeight}/>)}
        </div>
    );
}

export default createFragmentContainer(NoteFinderPageOverlay, {
    sheet: graphql`
    fragment NoteFinderPageOverlay_sheet on MusicSheet {
        id
        width
        height
        tie_finder_steps: steps(step_type: MUSIC_TIE_FINDER) {
            id
            data {
                ... on MusicParseStepDataTieFinder {
                    ties {
                        id
                        ...NoteFinderPageOverlayTie_Tie
                    }
                }
            }
        }
        bar_line_processor_steps: steps(step_type: BAR_LINE_PROCESSOR) {
            id
            status
            data {
                ... on MusicParseStepDataBarLineProcessor {
                    masks {
                        key: mask_storage_name
                        ...NoteFinderPageBarLineMask_mask
                    }
                }
            }
        }
        note_combine_steps: steps(step_type: NOTE_DATA_SOURCE_COMBINE) {
            id
            status
            data {
                ... on MusicParseStepDataNoteDataSourceCombine {
                    notes {
                        note_id
                        ...NoteFinderPageOverlayNote_Note
                    }
                    sharp_flat_naturals {
                        id
                        ...NoteFinderPageOverlaySharpFlatNatural_sharpFlatNatural
                    }
                }
            }  
        }
    }
    `
});